import jwtDecode from 'jwt-decode';
import firebase from 'firebase';
import API from 'constants/Api';

const TOKEN_REFRESH_INTERVAL = 60 * 1000; // check token validity every 1 min.
const TOKEN_REFRESH_THRESHOLD = 180; // if token lifetime is less than 180 sec then refresh it.

let intervalId = null;
let logoutFnHandler = () => {};

/**
 * Starts a background task to regularly validate and refresh the authentication token.
 * @param {() => void} logoutFunction - A callback that will be called if the user is no longer valid (e.g., deleted, disabled).
 * @returns {void}
 */
export const startTokenRefreshTask = (logoutFunction) => {
  if (intervalId !== null) return; // avoid double start

  if (logoutFunction) logoutFnHandler = logoutFunction;

  intervalId = setInterval(() => {
    if (shouldRefreshToken()) {
      refreshToken();
    }
  }, TOKEN_REFRESH_INTERVAL);
};

export const stopTokenRefreshTask = () => {
  clearInterval(intervalId);
  intervalId = null;
};

export const shouldRefreshToken = () => {
  const token = getAccessToken();
  if (!token) {
    console.warn('no token nothing to refresh, stopping token refreshing task');
    stopTokenRefreshTask();
    return false;
  }

  try {
    return checkIsTokenExpiring(token);
  } catch (err) {
    console.error('Invalid JWT', err);
    return false;
  }
};

const checkIsTokenExpiring = (token) => {
  const decodedInfo = jwtDecode(token);
  const secondsLeft = decodedInfo.exp - Math.floor(Date.now() / 1000);
  const isTokenExpiring = secondsLeft < TOKEN_REFRESH_THRESHOLD;
  if (isTokenExpiring) console.log('token will expire in ' + secondsLeft + ' seconds -> refreshing');
  return isTokenExpiring;
};

export const refreshToken = () => {
  firebase
    .auth()
    .currentUser.getIdToken(true)
    .then((token) => {
      console.log('token', 'new token acquired', token);
      localStorage.setItem(API.ACCESS_TOKEN_KEY, token);
    })
    .catch((error) => {
      console.error(error);

      switch (error.code) {
        case 'auth/user-disabled':
        case 'auth/user-not-found':
        case 'auth/invalid-user-token':
          terminateSessionOnAuthFailure();
          break;
        case 'auth/network-request-failed':
          console.error('Network error. Please check your connection.');
          break;
        case 'auth/internal-error':
          console.error('Internal error happened. Please contact support.');
          break;
        default:
          console.error('Unexpected error occurred. Please try again.');
      }
    });
};

export const getAccessToken = () => {
  return localStorage.getItem(API.ACCESS_TOKEN_KEY);
};

export const setAccessToken = (token) => {
  localStorage.setItem(API.ACCESS_TOKEN_KEY, token);
};

export const removeAccessToken = () => {
  return localStorage.removeItem(API.ACCESS_TOKEN_KEY);
};

export const ensureValidAuthToken = () => {
  const token = getAccessToken();
  if (!token) return false;

  try {
    if (checkIsTokenExpiring(token)) refreshToken();
  } catch (err) {
    console.error('Invalid JWT', err);
    return false;
  }
  
  return true;
};

/**
 * Terminates the current session due to an authentication failure.
 * @param {string} [message="Your account is no longer active. Please contact your administrator."]
 *     A user-facing message explaining why the session is terminated.
 */
export const terminateSessionOnAuthFailure = (message = 'Your account is no longer active. Please contact your administrator.') => {
  removeAccessToken();
  stopTokenRefreshTask();
  logoutFnHandler();
  alert(message);
};
